import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'admin-lte/dist/css/adminlte.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'admin-lte/dist/js/adminlte.min.js';
import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, LoadingScreen, useAuth } from './auth/AuthContext';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { SystemSettingsPage } from './pages/SystemSettingsPage';
import { GOOGLE_ANALYTICS_MEASUREMENT_ID, trackPageView } from './analytics';
import { ToastProvider } from './components/ToastProvider';
import './styles.css';

type ProtectedRouteProps = {
  children: JSX.Element;
  requiredRole?: 'admin' | 'standard';
};

function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { authToken, user, authChecked } = useAuth();
  const location = useLocation();

  if (!authChecked) {
    return <LoadingScreen message="Checking your session…" />;
  }

  if (!authToken || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname || '/' }} />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AnalyticsListener() {
  const location = useLocation();

  useEffect(() => {
    if (!GOOGLE_ANALYTICS_MEASUREMENT_ID) return;
    const path = `${location.pathname}${location.search}${location.hash}`;
    trackPageView(path);
  }, [location.pathname, location.search, location.hash]);

  return null;
}

function Root() {
  return (
    <ToastProvider>
      <AuthProvider>
        <BrowserRouter>
          <AnalyticsListener />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<Navigate to="/login" replace />} />
            <Route path="/verify" element={<Navigate to="/login" replace />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/*"
              element={
                <ProtectedRoute requiredRole="admin">
                  <SystemSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ToastProvider>
  );
}

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');
export const applicationRoot = createRoot(container);
applicationRoot.render(<Root />);
